/**
 * KanbanCard — draggable card.
 *
 * Uses @dnd-kit's `useDraggable` (not `useSortable` — initial pass skips
 * intra-column reordering; cards just live in column-insertion order).
 *
 * `data.columnId` is included in the drag payload so DndContext.onDragEnd
 * can compare source vs. drop target without consulting the items array.
 *
 * Keyboard accessibility comes for free via dnd-kit's KeyboardSensor +
 * default activator role; the listeners spread onto the wrapper makes the
 * whole card Tab-focusable and operable with arrow keys + space/enter.
 */
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Card, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type { BoardItem } from "./useBoardData";
import { STAGE_LABELS } from "../intakeDetails/types";

interface KanbanCardProps {
  item: BoardItem;
}

export function KanbanCard({ item }: KanbanCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: item.id,
      data: { columnId: item.columnId, kind: item.kind },
    });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <Card
        size="small"
        hoverable
        aria-label={`Drag ${item.title}`}
        styles={{ body: { padding: 12 } }}
      >
        <Typography.Text strong style={{ display: "block", marginBottom: 4 }}>
          {item.title}
        </Typography.Text>
        {item.kind === "intake" ? (
          <>
            {item.agency && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                {item.agency}
              </Typography.Text>
            )}
            <Tag color="blue" style={{ marginTop: 6 }}>
              {STAGE_LABELS[item.columnId] ?? item.columnId}
            </Tag>
          </>
        ) : (
          <>
            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              {item.status}
            </Typography.Text>
            {item.endDate && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                Ends {dayjs(item.endDate).format("MMM D, YYYY")}
              </Typography.Text>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
